import React, { useState, useEffect, useRef } from "react";
import "./UserForm.scss";
import { createlawyers, updateUsers } from "../../services/api/AddUsersApi";
import { useNavigate, useParams } from "react-router-dom";
import { getUserData } from "../../services/api/AuthApi";
import { BiErrorCircle } from "react-icons/bi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getUserRights,getVisibilityRights } from "../../services/api/GlobalSettingsApi";

const roles = ["moderator", "lawyer","admin","customer"];

export const UserForm = ({ onSuccess, isModal = false, onCancel = null }) => {
  const navigate = useNavigate();
  const { userid } = useParams();
  const isEditMode = Boolean(userid);
  const { logout } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phno: "",
    isActive: true,
    role: "lawyer",
    visibility: {},
  });

  const [errors, setErrors] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [availableVisibilityRights, setAvailableVisibilityRights] = useState({});

  // Add refs and state for focus management
  const nameInputRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const { showToast } = useToast();

  // Function to decode JWT and get user ID
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;

      // Decode JWT payload (base64 decode the middle part)
      const payload = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload));

      // Return user ID - based on your auth/me data structure, it should be _id
      return decodedPayload._id || decodedPayload.id || decodedPayload.userId || decodedPayload.sub;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const handleLogout = () => {
    logout();
    // Delay navigation to allow state updates to complete
    setTimeout(() => {
      navigate("/");
    }, 0);
  };

  // Add auto-focus effect
  useEffect(() => {
    // Prevent multiple focus attempts
    if (hasFocusedRef.current) return;

    // For editing mode, wait for initial data to load
    // For new user, focus immediately after component mounts
    const shouldFocus = isEditMode ? initialDataLoaded : true;
    
    if (!shouldFocus) return;

    // Add a small delay to ensure DOM is ready
    const focusTimeout = setTimeout(() => {
      const nameInput = nameInputRef.current;
      if (nameInput && !hasFocusedRef.current) {
        nameInput.focus();
        
        // Move cursor to end of existing text (useful for edit mode)
        const val = nameInput.value || '';
        try {
          nameInput.setSelectionRange(val.length, val.length);
        } catch (e) {
          // Ignore selection range errors
        }
        
        hasFocusedRef.current = true;
      }
    }, 150);

    return () => clearTimeout(focusTimeout);
  }, [initialDataLoaded, isEditMode, formData.name]); // Add formData.name to ensure focus after data loads

  useEffect(() => {
    // Get current user ID from access token when component mounts
    const userId = getUserIdFromToken();
    setCurrentUserId(userId);

    // Fetch visibility rights first
    const fetchVisibilityRights = async () => {
      try {
        const visibilityRes = await getVisibilityRights();
        const visibilityRights = visibilityRes.data;
        setAvailableVisibilityRights(visibilityRights);
        
        // Set default visibility based on API response for new users
        if (!isEditMode) {
          setFormData(prev => ({
            ...prev,
            visibility: { ...visibilityRights }
          }));
          setInitialDataLoaded(true);
        }
        
        return visibilityRights;
      } catch (error) {
        console.error('Error fetching visibility rights:', error);
        return {};
      }
    };

    // For edit mode, fetch both user data and visibility rights
    if (isEditMode) {
      Promise.all([
        getUserData(userid),
        getUserRights(userid),
      ]).then(([userRes, rightsRes]) => {
        const user = userRes.data;
        const userRights = rightsRes.data;
        
        setFormData({
          name: user.name || "",
          email: user.email || "",
          phno: user.phno || "",
          isActive: user.isActive ?? true,
          role: user.role || "lawyer",
          visibility: {
            ...userRights
          }
        });
        setInitialDataLoaded(true); // Mark as loaded
      }).catch((err) => {
        console.error('Error fetching user data or rights:', err);
      });
    } else {
      // For new user, just fetch visibility rights
      fetchVisibilityRights();
    }
  }, [isEditMode, userid]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/home/addusers');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone number
    if (name === 'phno') {
      // Only allow digits and limit to 10 characters
      const digitsOnly = value.replace(/\D/g, '');
      const limitedValue = digitsOnly.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
      return;
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = "Name is required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) errs.email = "Valid email is required";
    if (!formData.role) errs.role = "Role is required";
    if (formData.phno && formData.phno.length !== 10) errs.phno = "Phone number must be exactly 10 digits";
    if (formData.phno && !/^\d{10}$/.test(formData.phno)) errs.phno = "Phone number must contain only digits";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Add form validation function
  const isFormValid = () => {
    // Check required fields
    if (!formData.name || formData.name.trim() === '') return false;
    if (!formData.email || formData.email.trim() === '') return false;
    if (!formData.role || formData.role.trim() === '') return false;

    // Check email format
    if (!/\S+@\S+\.\S+/.test(formData.email)) return false;

    
    if (formData.phno && formData.phno.length > 0) {
      if (formData.phno.length !== 10 || !/^\d{10}$/.test(formData.phno)) return false;
    }

    return true;
  };

  // Update the handleSubmit function
  const handleSubmit = async (e) => {
    // allow calling handleSubmit programmatically without an event
    if (e && typeof e.preventDefault === "function") e.preventDefault();

    if (!isFormValid()) {
      showToast("Please fill in all required fields with valid information.", "error");
      return;
    }

    if (!validate()) return;

    if (isSaving) return; // Prevent multiple submissions
    setIsSaving(true);

    console.log(formData, "formuser");

    try {
      let response;
      
      if (isEditMode) {
        response = await updateUsers(userid, formData);
      } else {
        response = await createlawyers(formData);
      }

      // Check if response contains duplicate error (for both add and update)
      if (response?.data?.code === "duplicate" || response?.data?.message?.includes("already registered")) {
        showToast(response.data.message || "Email or Phone Number is already registered", "error");
        return;
      }

      // Success handling
      if (isEditMode) {
        // Check if user is editing their own profile
        if (userid === currentUserId) {
          showToast("Profile updated successfully. Logging out...", "success");

          // Use immediate logout without setTimeout to avoid React issues
          // The toast will still show briefly before logout
          requestAnimationFrame(() => {
            handleLogout();
          });

          return; // Exit early to prevent normal navigation
        } else {
          showToast("User updated successfully.", "success");
        }
      } else {
        // Reset form data and focus for new users
        setFormData({
          name: "",
          email: "",
          phno: "",
          isActive: true,
          role: "lawyer",
          visibility: { ...availableVisibilityRights },
        });
        hasFocusedRef.current = false; // Reset focus ref for new users
        
        showToast("User created successfully.", "success");
      }

      onSuccess?.();
      
      if (!isModal) {
        navigate("/home/addusers");
      }
    } catch (error) {
      console.error("Error saving user:", error);
      
      // Handle specific error messages from backend
      if (error.response?.data?.code === "duplicate" || error.response?.data?.message?.includes("already registered")) {
        showToast(error.response.data.message || "Email or Phone Number is already registered", "error");
      } else if (error.response?.data?.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast(isEditMode ? "Error updating user. Please try again." : "Error creating user. Please try again.", "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Enhanced Enter key submission handler
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      const type = target.type;
      
      // Don't submit from textareas, buttons, or checkboxes
      if (tagName === "textarea" || type === "button" || type === "submit" || type === "checkbox") {
        return;
      }
      
      // Prevent default form submission
      e.preventDefault();
      
      // Only submit if form is valid and not already saving
      if (isFormValid() && !isSaving) {
        handleSubmit();
      }
    }
  };

  return (
    <div className={`user-form-container ${isModal ? "modal-user-form-container" : ""}`}>
      <div className={`user-form-card ${isModal ? "modal-user-form-card" : ""}`}>
        <h1 className={`user-form-title ${isModal ? "modal-user-form-title" : ""}`}>
          {isEditMode ? "Update User Details" : "Create New User"}
        </h1>

        {isModal && (
          <button
            className="user-form-close-button"
            onClick={handleCancel}
            aria-label="Close"
          >
            &times;
          </button>
        )}

        <hr className="user-form-divider" />

        {/* Show warning if user is editing their own profile */}
        {isEditMode && userid === currentUserId && (
          <div className="warning-message">
            <BiErrorCircle /> You are editing your own profile. Changes to visibility settings will log you out.
          </div>
        )}

        <form className="user-form" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          {/* Basic Information Section */}
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="user-form-group">
              <label className="user-form-label">Name <span className="user-form-required">*</span></label>
              <input 
                ref={nameInputRef}  // Add the ref here
                type="text" 
                name="name"
                className="user-form-input"
                value={formData.name} 
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </div>

            <div className="user-form-flex-row">
              <div className="user-form-col-half">
                <label className="user-form-label">Email <span className="user-form-required">*</span></label>
                <input 
                  type="email" 
                  name="email"
                  className="user-form-input"
                  value={formData.email} 
                  onChange={handleChange}
                  placeholder="Enter email address"
                />
              </div>
              
              <div className="user-form-col-half">
                <label className="user-form-label">Phone No <span className="user-form-required">*</span></label>
                <input 
                  type="tel" 
                  name="phno"
                  className="user-form-input"
                  value={formData.phno} 
                  onChange={handleChange}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                  pattern="\d{10}"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          {/* User Settings Section */}
          <div className="form-section">
            <h3>User Settings</h3>
            
            <div className="user-form-flex-row">
              <div className="user-form-col-half">
                <label className="user-form-label">Status</label>
                <select 
                  name="isActive"
                  className="user-form-select"
                  value={formData.isActive ? "true" : "false"} 
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, isActive: e.target.value === "true" }));
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="user-form-col-half">
                <label className="user-form-label">Role <span className="user-form-required">*</span></label>
                <select 
                  name="role"
                  className="user-form-select"
                  value={formData.role} 
                  onChange={handleChange}
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Module Visibility Section */}
          <div className="form-section">
            <div className="visibility-section">
              <h3>Module Visibility</h3>
              <div className="visibility-toggles">
                {Object.entries(isEditMode ? formData.visibility : availableVisibilityRights).map(([key, value]) => (
                  <label key={key} className="toggle-label">
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isEditMode ? value : (formData.visibility[key] ?? value)}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            visibility: {
                              ...prev.visibility,
                              [key]: !prev.visibility[key],
                            },
                          }))
                        }
                      />
                      <span className="slider" />
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="user-form-button-container">
            <button
              type="submit"
              className="user-form-btn user-form-btn-save"
              disabled={!isFormValid() || isSaving}
            >
              {isSaving ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Save")}
            </button>
            <button
              type="button"
              className="user-form-btn-cancel"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};